import { getEnv } from '@/config/env';
import { sanitizeTelemetryEvent, type TelemetryEvent } from '@/telemetry';
import { isBatteryLevel } from './robotBattery';
import type {
  ApiErrorBody,
  ChatMessage,
  ChatResponse,
  ConnectionSessionResponse,
  RegisteredRobot,
  SpeechRequest,
  TranscribeCredentialsResponse,
  UserFlowIceResponse,
  WebSocketTicketResponse,
} from './types';

const HTTP_TIMEOUT_MS = 10_000;
const CHAT_TIMEOUT_MS = 12_000;

export class FiloApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = 'HTTP_ERROR',
    readonly retryable = false,
  ) {
    super(message);
    this.name = 'FiloApiError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  retries?: number;
  timeoutMs?: number;
  responseType?: 'json' | 'audio';
};

export const parseRegisteredRobots = (value: unknown): RegisteredRobot[] => {
  if (!Array.isArray(value)) throw new Error('ロボット一覧の形式が不正です');
  const robots = value.map((item) => {
    if (!item || typeof item !== 'object') throw new Error('ロボット情報の形式が不正です');
    const robot = item as Record<string, unknown>;
    if (
      typeof robot.robotId !== 'string'
      || robot.robotId.length === 0
      || typeof robot.displayName !== 'string'
      || robot.displayName.length === 0
      || !['pending', 'active', 'disabled'].includes(String(robot.status))
      || typeof robot.online !== 'boolean'
      || (robot.lastSeenAt !== undefined && typeof robot.lastSeenAt !== 'string')
      || (robot.batteryLevel !== undefined && !isBatteryLevel(robot.batteryLevel))
      || (robot.isCharging !== undefined && typeof robot.isCharging !== 'boolean')
      || Object.keys(robot).some((key) => ![
        'robotId', 'displayName', 'status', 'online', 'lastSeenAt', 'batteryLevel', 'isCharging',
      ].includes(key))
    ) throw new Error('ロボット情報の形式が不正です');
    return robot as RegisteredRobot;
  });
  if (new Set(robots.map(({ robotId }) => robotId)).size !== robots.length) throw new Error('ロボット一覧の形式が不正です');
  return robots;
};

export const parseTranscribeCredentials = (value: unknown): TranscribeCredentialsResponse => {
  if (!value || typeof value !== 'object') {
    throw new Error('文字起こし認証情報の形式が不正です');
  }
  const response = value as Record<string, unknown>;
  const credentials = response.credentials;
  if (
    typeof response.region !== 'string'
    || !response.region.trim()
    || !credentials
    || typeof credentials !== 'object'
  ) {
    throw new Error('文字起こし認証情報の形式が不正です');
  }
  const record = credentials as Record<string, unknown>;
  if (
    typeof record.accessKeyId !== 'string'
    || !record.accessKeyId
    || typeof record.secretAccessKey !== 'string'
    || !record.secretAccessKey
    || typeof record.sessionToken !== 'string'
    || !record.sessionToken
    || typeof record.expiration !== 'string'
    || !Number.isFinite(Date.parse(record.expiration))
  ) {
    throw new Error('文字起こし認証情報の形式が不正です');
  }
  return {
    region: response.region,
    credentials: {
      accessKeyId: record.accessKeyId,
      secretAccessKey: record.secretAccessKey,
      sessionToken: record.sessionToken,
      expiration: record.expiration,
    },
  };
};

export class FiloApiClient {
  constructor(private readonly getAccessToken: () => Promise<string>) {}

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { apiUrl } = getEnv();
    const {
      body,
      retries: configuredRetries,
      timeoutMs = HTTP_TIMEOUT_MS,
      responseType = 'json',
      signal,
      ...requestInit
    } = options;
    const serializedBody = body === undefined ? undefined : JSON.stringify(body);
    const retries = configuredRetries ?? (options.method === undefined || options.method === 'GET' ? 2 : 0);
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) controller.abort();
      const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        const token = await this.getAccessToken();
        controller.signal.throwIfAborted();
        const response = await fetch(`${apiUrl}${path}`, {
          ...requestInit,
          body: serializedBody,
          headers: {
            Accept: responseType === 'audio' ? 'audio/wav' : 'application/json',
            Authorization: `Bearer ${token}`,
            ...(serializedBody === undefined ? {} : { 'Content-Type': 'application/json' }),
            ...options.headers,
          },
          signal: controller.signal,
        });
        if (response.ok) {
          if (response.status === 204) return undefined as T;
          if (responseType === 'audio') {
            if (!response.headers.get('Content-Type')?.startsWith('audio/wav')) {
              throw new Error('TTS APIから音声を取得できませんでした');
            }
            return await response.arrayBuffer() as T;
          }
          return await response.json() as T;
        }
        const body = await response.json().catch(() => ({})) as ApiErrorBody;
        throw new FiloApiError(
          body.error?.message || `API request failed (${response.status})`,
          response.status,
          body.error?.code,
          body.error?.retryable,
        );
      } catch (error) {
        lastError = error;
        const retryable = error instanceof FiloApiError ? error.status >= 500 : error instanceof DOMException && error.name === 'AbortError';
        if (signal?.aborted || attempt >= retries || !retryable) throw error;
        await new Promise((resolve) => window.setTimeout(resolve, 250 * 2 ** attempt));
      } finally {
        window.clearTimeout(timeout);
        signal?.removeEventListener('abort', abort);
      }
    }
    throw lastError;
  }

  async getRobots(): Promise<RegisteredRobot[]> {
    const response = await this.request<{ robots: RegisteredRobot[] }>('/robots');
    return parseRegisteredRobots(response.robots);
  }

  createConnectionSession(robotIds: string[], clientRequestId: string = crypto.randomUUID()) {
    const body = { robotIds, clientRequestId };
    return this.request<ConnectionSessionResponse>('/connection-sessions', {
      method: 'POST',
      body,
      retries: 2,
    });
  }

  createUserFlowIce(): Promise<UserFlowIceResponse> {
    return this.request<UserFlowIceResponse>('/user-flow-ice', {
      method: 'POST',
      body: {},
      retries: 2,
    });
  }

  refreshConnectionSession(sessionId: string) {
    return this.request<ConnectionSessionResponse>(`/connection-sessions/${encodeURIComponent(sessionId)}/refresh`, {
      method: 'POST',
      body: {},
      retries: 2,
    });
  }

  closeConnectionSession(sessionId: string) {
    return this.request<void>(`/connection-sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      retries: 2,
    });
  }

  reportTelemetry(event: TelemetryEvent): Promise<void> {
    return this.request<unknown>('/telemetry', {
      method: 'POST',
      body: sanitizeTelemetryEvent(event),
      retries: 0,
    }).then(() => undefined).catch(() => undefined);
  }

  createWebSocketTicket() {
    return this.request<WebSocketTicketResponse>('/ws-tickets', { method: 'POST', body: {} });
  }

  createChatResponse(messages: ChatMessage[]) {
    return this.request<ChatResponse>('/chat', {
      method: 'POST',
      body: { messages },
      retries: 0,
      timeoutMs: CHAT_TIMEOUT_MS,
    });
  }

  synthesizeSpeech(data: SpeechRequest, signal?: AbortSignal): Promise<ArrayBuffer> {
    return this.request<ArrayBuffer>('/tts', {
      method: 'POST',
      body: data,
      responseType: 'audio',
      timeoutMs: 30_000,
      signal,
      retries: 0,
    });
  }

  async createTranscribeCredentials() {
    const response = await this.request<unknown>('/transcribe-credentials', {
      method: 'POST',
      body: {},
      retries: 2,
    });
    return parseTranscribeCredentials(response);
  }
}
